import React, { useState } from 'react';
import { Card, Button, Alert, Badge } from 'react-bootstrap';
import { Wifi, WifiOff, AlertCircle, Loader } from 'lucide-react';
import whatsappService from '../../services/whatsappService';

type ConnectionStatus = 'untested' | 'testing' | 'success' | 'error';

interface ConnectionTestSectionProps {
    lastValidated?: string | null;
    validationError?: string | null;
}

/**
 * Connection Test Section Component
 * Allows testing the WhatsApp API connection
 */
const ConnectionTestSection: React.FC<ConnectionTestSectionProps> = ({
    lastValidated,
    validationError
}) => {
    const [status, setStatus] = useState<ConnectionStatus>('untested');
    const [message, setMessage] = useState<string>('');

    const getStatusInfo = () => {
        if (status === 'testing') {
            return {
                icon: <Loader size={20} className="spin" />,
                variant: 'secondary',
                text: 'Testando...',
                badgeVariant: 'secondary' as const
            };
        }
        if (status === 'success') {
            return {
                icon: <Wifi size={20} />,
                variant: 'success',
                text: 'Conectado',
                badgeVariant: 'success' as const
            };
        }
        if (status === 'error') {
            return {
                icon: <WifiOff size={20} />,
                variant: 'danger',
                text: 'Desconectado',
                badgeVariant: 'danger' as const
            };
        }
        return {
            icon: <AlertCircle size={20} />,
            variant: 'warning',
            text: 'Não testado',
            badgeVariant: 'warning' as const
        };
    };

    const handleTest = async () => {
        setStatus('testing');
        setMessage('');

        const result = await whatsappService.testConnection();

        if (result.success) {
            setStatus('success');
            setMessage(result.message);
        } else {
            setStatus('error');
            setMessage(result.message);
        }
    };

    const statusInfo = getStatusInfo();

    return (
        <Card className="mt-4">
            <Card.Header className="d-flex align-items-center justify-content-between">
                <h6 className="mb-0">Status da Conexão</h6>
                <Badge bg={statusInfo.badgeVariant} className="d-flex align-items-center gap-2">
                    {statusInfo.icon} {statusInfo.text}
                </Badge>
            </Card.Header>
            <Card.Body>
                {lastValidated && (
                    <p className="text-muted mb-2">
                        <small>Última validação: {new Date(lastValidated).toLocaleString('pt-BR')}</small>
                    </p>
                )}

                {validationError && status === 'untested' && (
                    <Alert variant="danger" className="mb-3">
                        <strong>Erro na última validação:</strong><br />
                        {validationError}
                    </Alert>
                )}

                {message && status !== 'untested' && (
                    <Alert variant={status === 'success' ? 'success' : 'danger'} className="mb-3">
                        {status === 'success' ? '✅' : '❌'} {message}
                    </Alert>
                )}

                <Button
                    variant="primary"
                    onClick={handleTest}
                    disabled={status === 'testing'}
                    className="d-flex align-items-center gap-2"
                >
                    {status === 'testing' ? (
                        <>
                            <Loader size={18} className="spin" /> Testando...
                        </>
                    ) : (
                        <>
                            <Wifi size={18} /> Testar Conexão
                        </>
                    )}
                </Button>

                <small className="text-muted d-block mt-3">
                    Salve a configuração antes de testar a conexão.
                </small>
            </Card.Body>
        </Card>
    );
};

export default ConnectionTestSection;
